import { Color, TextVariant } from 'styles/types'
import styled, { css } from 'styled-components/macro'
import { respondAbove } from 'styles/mediaQueries'
import { Spacing, spacingMixin } from 'styles/spacing'
import { resolveResponsiveProp } from 'styles/mixins'
import { shouldForwardProp } from 'helpers'

export type Align = 'left' | 'right' | 'center'
export interface TextProps extends Spacing {
    variant: TextVariant
    color?: Color
    hasTransition?: boolean
    align?: Align | [Align, Align]
    isUnderlined?: boolean
    isStrikeThrough?: boolean
    isCapitalized?: boolean
    truncateAfter?: number
    hasNoWrap?: boolean
}

const Text = styled.p.withConfig({
    shouldForwardProp: (prop) =>
        shouldForwardProp<TextProps>(
            [
                'truncateAfter',
                'variant',
                'hasTransition',
                'align',
                'isUnderlined',
                'isCapitalized',
                'hasNoWrap',
                'color',
                'isStrikeThrough',
            ],
            prop
        ),
})<TextProps>`
    color: ${({ color, theme, variant }) => {
        const defaultColor = variant?.includes('heading')
            ? theme.colors.grayscale_900
            : theme.colors.grayscale_500
        return color ? theme.colors[color] : defaultColor
    }};
    font-size: ${({ theme, variant }) =>
        theme.textVariants[variant].fontSizeMobile}px;
    line-height: ${({ theme, variant }) =>
        theme.textVariants[variant].lineHeightMobile}px;
    font-family: ${({ theme, variant }) =>
        theme.textVariants[variant].fontFamily};
    font-weight: ${({ theme, variant }) =>
        theme.textVariants[variant].fontWeight};
    letter-spacing: ${({ theme, variant }) =>
        theme.textVariants[variant].letterSpacing};

    ${({ hasTransition }) =>
        hasTransition &&
        css`
            transition: color 100ms ease-in;
        `}

    ${({ align }) =>
        align &&
        resolveResponsiveProp({
            type: 'text-align',
            value: align,
        })}

    ${({ hasNoWrap }) =>
        hasNoWrap &&
        css`
            white-space: nowrap;
        `}
    ${({ isCapitalized }) =>
        isCapitalized &&
        css`
            &::first-letter {
                text-transform: capitalize;
            }
        `}
    ${({ isUnderlined }) =>
        isUnderlined &&
        css`
            text-decoration: underline;
        `}
    ${({ isStrikeThrough }) =>
        isStrikeThrough &&
        css`
            text-decoration: line-through;
        `}

    ${({ theme }) =>
        theme.mode === 'dark' &&
        css`
            a {
                color: ${theme.colors.primary_500} !important;
            }
        `}

    @media ${respondAbove.xl} {
        font-size: ${({ theme, variant }) =>
            theme.textVariants[variant].fontSizeDesktop}px;
        line-height: ${({ theme, variant }) =>
            theme.textVariants[variant].lineHeightDesktop}px;
    }

    ${spacingMixin}

    &:focus-visible {
        color: ${({ theme }) => theme.colors.primary_500};
    }

    ${({ truncateAfter }) =>
        truncateAfter &&
        css`
            display: -webkit-box;
            -webkit-line-clamp: ${truncateAfter};
            -webkit-box-orient: vertical;
            overflow: hidden;
        `}
`

export default Text
